// src/components/ro/ROActivityDrawer.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useROMessages } from "../../context/ROMessagesContext";
import { useROUnread } from "../../context/ROUnreadContext";
import { useROStore } from "../../context/ROContext";
import { useRealtime } from "../../context/RealtimeContext";
import ROStatusTimeline from "./ROStatusTimeline";
import {
  createAttachmentUploadRequestId,
  isRetryableAttachmentUploadError,
} from "../../utils/attachmentUpload";
import {
  filterLegacyNotesForStructuredMessages,
  splitLegacyNoteBlocks,
} from "../../utils/roMessageLegacy";
import {
  canShowUndoSend,
  undoSendRemainingSeconds,
} from "../../utils/undoSend";
import { mergeAttachmentsById } from "../../utils/roAttachmentMerge";

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function safeDate(input) {
  if (!input) return null;
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatWhen(input) {
  const d = safeDate(input);
  if (!d) return "";
  return d.toLocaleString([], {
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function timeValue(input) {
  const d = safeDate(input);
  return d ? d.getTime() : null;
}

function isLikelyDateText(value) {
  const text = safeString(value).trim();
  if (!text) return false;

  const d = safeDate(text);
  if (d) return true;

  return (
    /\d{1,2}\/\d{1,2}\/\d{2,4}/.test(text) ||
    /\d{4}-\d{2}-\d{2}/.test(text)
  );
}

function stripRoleSuffix(value) {
  const raw = safeString(value).trim();
  if (!raw) return "";

  return raw
    .replace(/\s*\((admin|manager|service manager|advisor|service advisor|technician|tech|parts|parts advisor|parts manager|foreman|shop foreman|dispatcher|staff|user|support|super admin)\)\s*$/i, "")
    .trim();
}

function cleanAuthorName(value) {
  const raw = stripRoleSuffix(value);
  if (!raw) return "User";

  const lowered = raw.toLowerCase();
  if (lowered === "unknown" || lowered === "note") return "User";

  return raw;
}

function getTag(ro) {
  return safeString(
    ro?.tagNumber ??
      ro?.tag_number ??
      ro?.tag ??
      ro?.keyTag ??
      ro?.key_tag ??
      ""
  ).trim();
}

function getROLabel(ro) {
  const roNumber = safeString(ro?.roNumber || ro?.ro_number).trim();
  return roNumber ? `RO #${roNumber}` : "Repair Order";
}

function getCustomerVehicleLine(ro) {
  const customer = safeString(ro?.customerName || ro?.customer_name).trim();
  const vehicle = safeString(ro?.vehicle).trim();

  if (customer && vehicle) return `${customer} \u2014 ${vehicle}`;
  if (customer) return customer;
  if (vehicle) return vehicle;
  return "Customer / vehicle details";
}

function getAdvisorName(ro) {
  return safeString(
    ro?.advisorName || ro?.serviceAdvisorName || ro?.writerName || ""
  ).trim();
}

function getTechNameForRO(ro, getTechNameById) {
  const direct = safeString(
    ro?.assignedTechName ||
      ro?.assigned_tech_name ||
      ro?.techName ||
      ro?.technicianName ||
      ""
  ).trim();

  if (direct) return direct;

  const techId = safeString(
    ro?.assignedTechId ||
      ro?.assigned_tech_id ||
      ro?.techId ||
      ro?.technicianId ||
      ""
  ).trim();

  if (!techId) return "";

  if (typeof getTechNameById === "function") {
    return safeString(getTechNameById(techId)).trim();
  }

  return techId;
}

function getStatusText(ro) {
  const status = safeString(ro?.status).trim();
  const lifecycle = safeString(ro?.lifecycle || ro?.lifeCycle).trim();

  const pickupStatus = safeString(
    ro?.customerPickupStatus || ro?.customer_pickup_status
  ).trim();

  const advisorAction = safeString(
    ro?.advisorActionStatus || ro?.advisor_action_status
  ).trim();

  return (
    pickupStatus ||
    advisorAction ||
    status ||
    lifecycle ||
    "Current status"
  )
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .toLowerCase()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function splitNoteBlocks(notes) {
  return splitLegacyNoteBlocks(notes);
}

function parseNoteHeader(header) {
  const text = safeString(header).trim();

  if (!text) {
    return {
      author: "User",
      when: "",
    };
  }

  const match = text.match(/^(.+?)\s*[-\u2013\u2014]\s*(.+)$/);

  if (!match) {
    return {
      author: cleanAuthorName(text),
      when: "",
    };
  }

  const left = safeString(match[1]).trim();
  const right = safeString(match[2]).trim();

  const leftLooksDate = isLikelyDateText(left);
  const rightLooksDate = isLikelyDateText(right);

  if (leftLooksDate && !rightLooksDate) {
    return {
      author: cleanAuthorName(right),
      when: left,
    };
  }

  if (rightLooksDate && !leftLooksDate) {
    return {
      author: cleanAuthorName(left),
      when: right,
    };
  }

  return {
    author: cleanAuthorName(right || left),
    when: leftLooksDate ? left : "",
  };
}

function fileSizeLabel(bytes) {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return "";

  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;

  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function mediaLabel(attachment) {
  const type = safeString(attachment?.fileType).toLowerCase();
  if (type === "photo") return "Photo";
  if (type === "video") return "Video";
  return "Attachment";
}

function buildThreadItems(notes, attachments, messages) {
  const noteItems = (Array.isArray(notes) ? notes : []).map((note, index) => {
    const parsed = parseNoteHeader(note.header);
    const timestamp = timeValue(parsed.when);

    return {
      kind: "note",
      id: `note-${note.id || index}`,
      note,
      parsed,
      timestamp,
      fallbackOrder: index,
    };
  });

  const attachmentItems = (Array.isArray(attachments) ? attachments : []).map(
    (attachment, index) => {
      const timestamp = timeValue(attachment?.createdAt);

      return {
        kind: "attachment",
        id: `attachment-${attachment?.id || index}`,
        attachment,
        timestamp,
        fallbackOrder: noteItems.length + index,
      };
    }
  );

  const messageItems = (Array.isArray(messages) ? messages : []).map(
    (message, index) => {
      const timestamp = timeValue(message?.createdAt || message?.localCreatedAt);

      return {
        kind: "message",
        id: `message-${message?.id || message?.localId || index}`,
        message,
        timestamp,
        fallbackOrder: noteItems.length + attachmentItems.length + index,
      };
    }
  );

  return [...noteItems, ...attachmentItems, ...messageItems].sort((a, b) => {
    const at = a.timestamp;
    const bt = b.timestamp;

    if (at != null && bt != null && at !== bt) return at - bt;
    if (at != null && bt == null) return 1;
    if (at == null && bt != null) return -1;

    return a.fallbackOrder - b.fallbackOrder;
  });
}

function overlayStyle() {
  return {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.44)",
    zIndex: 99999,
    display: "flex",
    justifyContent: "flex-end",
  };
}

function drawerStyle() {
  return {
    width: "min(760px, 96vw)",
    height: "100vh",
    background: "#ffffff",
    borderLeft: "1px solid #dbe3ee",
    boxShadow: "-24px 0 60px rgba(15,23,42,0.24)",
    display: "grid",
    gridTemplateRows: "auto minmax(0, 1fr) auto",
  };
}

function headerStyle() {
  return {
    padding: "16px 18px 14px",
    borderBottom: "1px solid #e5e7eb",
    display: "grid",
    gap: 10,
    background: "linear-gradient(180deg, #ffffff 0%, #fbfbfa 100%)",
  };
}

function metaPillStyle() {
  return {
    display: "inline-flex",
    alignItems: "center",
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
    color: "#334155",
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 11,
    fontWeight: 900,
    lineHeight: 1.1,
    whiteSpace: "nowrap",
  };
}

function closeButtonStyle() {
  return {
    border: "1px solid #d1d5db",
    background: "#ffffff",
    color: "#111827",
    borderRadius: 10,
    padding: "8px 10px",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 12,
  };
}

function tabButtonStyle(active) {
  return {
    border: active ? "1px solid #111827" : "1px solid #d1d5db",
    background: active ? "#111827" : "#ffffff",
    color: active ? "#ffffff" : "#111827",
    borderRadius: 999,
    padding: "7px 12px",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 12,
  };
}

function noteBubbleStyle() {
  return {
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: "11px 12px",
    background: "#ffffff",
    boxShadow: "0 2px 8px rgba(15,23,42,0.035)",
  };
}

function attachmentBubbleStyle() {
  return {
    border: "1px solid #dbe3ee",
    borderRadius: 14,
    padding: "11px 12px",
    background: "#ffffff",
    boxShadow: "0 2px 8px rgba(15,23,42,0.035)",
  };
}

function emptyStateStyle() {
  return {
    padding: 14,
    border: "1px dashed #cbd5e1",
    borderRadius: 14,
    background: "#f8fafc",
    color: "#64748b",
    fontSize: 12.5,
    lineHeight: 1.4,
  };
}

function textareaStyle() {
  return {
    width: "100%",
    minHeight: 74,
    resize: "vertical",
    border: "1px solid #d1d5db",
    borderRadius: 12,
    padding: "10px 12px",
    fontSize: 13,
    lineHeight: 1.4,
    outline: "none",
    boxSizing: "border-box",
  };
}

function primaryButtonStyle(disabled = false) {
  return {
    border: "1px solid #111827",
    background: disabled ? "#f3f4f6" : "#111827",
    color: disabled ? "#9ca3af" : "#ffffff",
    borderRadius: 10,
    padding: "10px 14px",
    cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: 900,
    fontSize: 12.5,
  };
}

function secondaryButtonStyle(disabled = false) {
  return {
    border: "1px solid #d1d5db",
    background: disabled ? "#f9fafb" : "#ffffff",
    color: disabled ? "#9ca3af" : "#111827",
    borderRadius: 10,
    padding: "10px 12px",
    cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: 900,
    fontSize: 12.5,
  };
}

function inlineLinkButtonStyle(disabled = false) {
  return {
    border: "1px solid #dbe3ee",
    background: disabled ? "#f9fafb" : "#ffffff",
    color: disabled ? "#9ca3af" : "#111827",
    borderRadius: 10,
    padding: "7px 9px",
    cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: 900,
    fontSize: 11.5,
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };
}

const SIGNED_URL_CACHE_MS = 8 * 60 * 1000;
const signedUrlCache = new Map();

function readSignedUrlCache(attachmentId) {
  const id = safeString(attachmentId).trim();
  if (!id) return "";

  const cached = signedUrlCache.get(id);

  if (!cached || !cached.url || Date.now() > cached.expiresAt) {
    signedUrlCache.delete(id);
    return "";
  }

  return cached.url;
}

function writeSignedUrlCache(attachmentId, url) {
  const id = safeString(attachmentId).trim();
  const cleanUrl = safeString(url).trim();

  if (!id || !cleanUrl) return;

  signedUrlCache.set(id, {
    url: cleanUrl,
    expiresAt: Date.now() + SIGNED_URL_CACHE_MS,
  });
}

function clearSignedUrlCache(attachmentId) {
  const id = safeString(attachmentId).trim();
  if (id) signedUrlCache.delete(id);
}

function AttachmentBubble({
  attachment,
  getSignedUrl,
  currentUserId,
  readOnly,
  nowMs,
  retracting,
  expired,
  onRetract,
}) {
  const attachmentId = safeString(attachment?.id).trim();
  const fileType = safeString(attachment?.fileType).toLowerCase();
  const fileName = safeString(attachment?.fileName).trim() || "Attachment";
  const createdAt = attachment?.createdAt;
  const author = safeString(attachment?.uploadedByName).trim() || "User";
  const role = safeString(attachment?.uploadedByRole).trim();
  const size = fileSizeLabel(attachment?.fileSize);
  const label = mediaLabel(attachment);
  const remainingSeconds = undoSendRemainingSeconds(createdAt, nowMs);
  const canUndo = canShowUndoSend({
    createdAt,
    ownerUserId: attachment?.uploadedByUserId,
    currentUserId,
    isRetracted: attachment?.isRetracted,
    isReadOnly: readOnly,
    isCanonical: !!attachmentId,
    nowMs,
  }) && expired !== true;

  const getSignedUrlRef = useRef(getSignedUrl);

  const [url, setUrl] = useState(() => readSignedUrlCache(attachmentId));
  const [urlError, setUrlError] = useState("");
  const [loadingUrl, setLoadingUrl] = useState(
    () => !readSignedUrlCache(attachmentId)
  );

  useEffect(() => {
    getSignedUrlRef.current = getSignedUrl;
  }, [getSignedUrl]);

  useEffect(() => {
    let alive = true;

    async function loadUrl() {
      if (!attachmentId || typeof getSignedUrlRef.current !== "function") {
        if (alive) setLoadingUrl(false);
        return;
      }

      const cachedUrl = readSignedUrlCache(attachmentId);

      if (cachedUrl) {
        if (alive) {
          setUrl(cachedUrl);
          setUrlError("");
          setLoadingUrl(false);
        }
        return;
      }

      try {
        if (alive) {
          setLoadingUrl(true);
          setUrlError("");
        }

        const nextUrl = await Promise.resolve(
          getSignedUrlRef.current(attachmentId)
        );

        const cleanUrl = safeString(nextUrl).trim();

        if (cleanUrl) {
          writeSignedUrlCache(attachmentId, cleanUrl);
        }

        if (alive) {
          setUrl(cleanUrl);
        }
      } catch (err) {
        console.error(err);

        if (alive) {
          setUrlError("Unable to load attachment preview.");
          setUrl("");
        }
      } finally {
        if (alive) {
          setLoadingUrl(false);
        }
      }
    }

    loadUrl();

    return () => {
      alive = false;
    };
  }, [attachmentId]);

  return (
    <div style={attachmentBubbleStyle()}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          alignItems: "baseline",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 950,
              color: "#111827",
            }}
          >
            {author}
            {role ? (
              <span style={{ color: "#64748b", fontWeight: 800 }}>
                {" "}
                · {role}
              </span>
            ) : null}
          </div>

          <div
            style={{
              marginTop: 3,
              fontSize: 11.5,
              color: "#64748b",
              wordBreak: "break-word",
            }}
          >
            {label}: {fileName}
            {size ? ` · ${size}` : ""}
          </div>
        </div>

        {createdAt ? (
          <div
            style={{
              fontSize: 11,
              color: "#64748b",
              whiteSpace: "nowrap",
            }}
          >
            {formatWhen(createdAt)}
          </div>
        ) : null}
      </div>

      <div style={{ marginTop: 10 }}>
        {loadingUrl && !url ? (
          <div style={emptyStateStyle()}>Loading attachment preview...</div>
        ) : urlError ? (
          <div style={emptyStateStyle()}>{urlError}</div>
        ) : url && fileType === "photo" ? (
          <a href={url} target="_blank" rel="noreferrer">
            <img
              src={url}
              alt={fileName}
              loading="lazy"
              style={{
                width: "100%",
                maxHeight: 320,
                objectFit: "contain",
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                background: "#f8fafc",
                display: "block",
              }}
            />
          </a>
        ) : url && fileType === "video" ? (
          <video
            src={url}
            controls
            preload="metadata"
            style={{
              width: "100%",
              maxHeight: 320,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              background: "#111827",
              display: "block",
            }}
          />
        ) : url ? (
          <div style={emptyStateStyle()}>
            Preview is not available for this file type.
          </div>
        ) : (
          <div style={emptyStateStyle()}>
            Attachment preview is not available.
          </div>
        )}
      </div>

      <div
        style={{
          marginTop: 9,
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            style={inlineLinkButtonStyle(false)}
          >
            Open
          </a>
        ) : null}

        {canUndo ? (
          <button
            type="button"
            onClick={() => onRetract?.(attachment)}
            disabled={retracting}
            style={inlineLinkButtonStyle(retracting)}
          >
            Undo Send {"\u00b7"} {remainingSeconds}s
          </button>
        ) : null}

        <span
          style={{
            color: "#94a3b8",
            fontSize: 11,
            fontWeight: 750,
          }}
        >
          Stored in the RO audit trail.
        </span>
      </div>
    </div>
  );
}

function statusText(message) {
  if (message?.isRetracted) return "Message withdrawn";
  if (message?.status === "sending") return "Sending\u2026";
  if (message?.status === "failed" && message?.retryable) {
    return "Failed \u2014 Retry";
  }
  if (message?.status === "failed") {
    return (
      safeString(message?.error).trim() ||
      "Couldn\u2019t retry that note. Send it again as a new note."
    );
  }
  return "Sent";
}

function MessageBubble({
  message,
  onRetry,
  onRetract,
  readOnly,
  currentUserId,
  nowMs,
  retracting,
  expired,
}) {
  const author = safeString(message?.authorDisplayName).trim() || "User";
  const role = safeString(message?.authorRole).trim();
  const when = message?.createdAt || message?.localCreatedAt;
  const displayTime = formatWhen(when) || safeString(when).trim();
  const failed = message?.status === "failed";
  const isRetracted = message?.isRetracted === true;
  const retryable = failed && message?.retryable && !readOnly && !isRetracted;
  const isCanonical = !!safeString(message?.id).trim() && message?.status === "sent";
  const remainingSeconds = undoSendRemainingSeconds(when, nowMs);
  const canUndo = canShowUndoSend({
    createdAt: when,
    ownerUserId: message?.authorUserId,
    currentUserId,
    isRetracted,
    isReadOnly: readOnly,
    isCanonical,
    nowMs,
  }) && expired !== true;

  return (
    <div style={noteBubbleStyle()}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          alignItems: "baseline",
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 950,
            color: "#111827",
          }}
        >
          {author}
          {role ? (
            <span style={{ color: "#64748b", fontWeight: 800 }}>
              {" "}
              {"\u00b7"} {role}
            </span>
          ) : null}
        </div>

        {displayTime ? (
          <div
            style={{
              fontSize: 11,
              color: "#64748b",
              whiteSpace: "nowrap",
            }}
          >
            {displayTime}
          </div>
        ) : null}
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize: 13,
          color: "#374151",
          lineHeight: 1.45,
          whiteSpace: "pre-wrap",
        }}
      >
        {isRetracted ? "Message withdrawn" : safeString(message?.body)}
      </div>

      <div
        style={{
          marginTop: 8,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 8,
          color: failed ? "#991b1b" : "#64748b",
          fontSize: 11.5,
          fontWeight: 850,
        }}
      >
        {retryable ? (
          <button
            type="button"
            onClick={() => onRetry?.(message)}
            style={inlineLinkButtonStyle(false)}
          >
            {statusText(message)}
          </button>
        ) : canUndo ? (
          <button
            type="button"
            onClick={() => onRetract?.(message)}
            disabled={retracting}
            style={inlineLinkButtonStyle(retracting)}
          >
            Undo Send {"\u00b7"} {remainingSeconds}s
          </button>
        ) : (
          <span>{statusText(message)}</span>
        )}
      </div>
    </div>
  );
}

export default function ROActivityDrawer({
  ro,
  isOpen,
  onClose,
  onMessageConfirmed,
  currentUser,
  actorRole,
  actorName,
  getTechName,
  title = "RO Thread",
  readOnly = false,
}) {
  const store = useROStore();
  const {
    getThreadState,
    ensureMessages,
    refreshMessages,
    loadOlderMessages,
    sendMessage,
    retryMessage,
    retractMessage,
    canSendMessages,
    isReadOnly: messagesReadOnly,
    maxBodyLength,
  } = useROMessages();
  const realtime = useRealtime();
  const {
    markRead,
    canMutateReadState,
    isDecimalMessageId,
    compareDecimalStrings,
  } = useROUnread();
  const fileInputRef = useRef(null);
  const submitLockRef = useRef(false);

  const [tab, setTab] = useState("thread");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentError, setAttachmentError] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [retractingMessageId, setRetractingMessageId] = useState("");
  const [retractingAttachmentId, setRetractingAttachmentId] = useState("");
  const [undoError, setUndoError] = useState("");
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [expiredMessageIds, setExpiredMessageIds] = useState(() => new Set());
  const [expiredAttachmentIds, setExpiredAttachmentIds] = useState(() => new Set());
  const [failedUploadAttempt, setFailedUploadAttempt] = useState(null);
  const [documentVisible, setDocumentVisible] = useState(() => {
    if (typeof document === "undefined") return true;
    return document.visibilityState === "visible";
  });
  const activeRoIdRef = useRef("");
  const attachmentRequestGenerationRef = useRef(0);
  const retractedAttachmentIdsRef = useRef(new Set());

  const roId = safeString(ro?.id).trim();

  const listAttachmentsForRO = store?.listAttachmentsForRO;
  const uploadAttachmentsForRO = store?.uploadAttachmentsForRO;
  const getAttachmentSignedUrl = store?.getAttachmentSignedUrl;
  const retractAttachment = store?.retractAttachment;
  const applyServerROSnapshot = store?.applyServerROSnapshot;
  const currentUserId = safeString(currentUser?.id || currentUser?.userId).trim();
  const listAttachmentsForRORef = useRef(listAttachmentsForRO);

  const threadState = getThreadState(roId);
  const legacyNotes = useMemo(() => splitNoteBlocks(ro?.notes), [ro?.notes]);
  const notes = useMemo(
    () =>
      filterLegacyNotesForStructuredMessages(
        legacyNotes,
        threadState.canonicalMessages
      ),
    [legacyNotes, threadState.canonicalMessages]
  );
  const threadMessages = useMemo(
    () => threadState.messages || [],
    [threadState.messages]
  );
  const newestCanonicalMessageId = useMemo(() => {
    return (Array.isArray(threadState.canonicalMessages)
      ? threadState.canonicalMessages
      : []
    ).reduce((latest, message) => {
      const messageId = safeString(message?.id).trim();
      if (!isDecimalMessageId(messageId)) return latest;
      if (!latest || compareDecimalStrings(messageId, latest) > 0) {
        return messageId;
      }
      return latest;
    }, "");
  }, [
    compareDecimalStrings,
    isDecimalMessageId,
    threadState.canonicalMessages,
  ]);
  const drawerReadOnly = readOnly || messagesReadOnly;
  const sendDisabled =
    busy ||
    drawerReadOnly ||
    !canSendMessages ||
    !safeString(draft).trim() ||
    safeString(draft).trim().length > maxBodyLength;

  const markAttachmentRetracted = useCallback((attachmentId) => {
    const cleanId = safeString(attachmentId).trim();
    if (!cleanId) return;
    retractedAttachmentIdsRef.current.add(cleanId);
  }, []);

  const filterKnownRetractedAttachments = useCallback((items) => {
    const retractedIds = retractedAttachmentIdsRef.current;
    return (Array.isArray(items) ? items : []).filter((attachment) => {
      const id = safeString(attachment?.id).trim();
      return !id || !retractedIds.has(id);
    });
  }, []);

  useEffect(() => {
    activeRoIdRef.current = isOpen ? roId : "";
  }, [isOpen, roId]);

  const refreshAttachments = useCallback(async () => {
    if (!roId || typeof listAttachmentsForRORef.current !== "function") return;

    const requestedRoId = roId;
    const requestGeneration = attachmentRequestGenerationRef.current + 1;
    attachmentRequestGenerationRef.current = requestGeneration;
    const next = await Promise.resolve(listAttachmentsForRORef.current(requestedRoId));
    if (
      !isOpen ||
      activeRoIdRef.current !== requestedRoId ||
      attachmentRequestGenerationRef.current !== requestGeneration
    ) {
      return;
    }
    setAttachments((current) =>
      filterKnownRetractedAttachments(mergeAttachmentsById(current, next))
    );
  }, [filterKnownRetractedAttachments, isOpen, roId]);

  const refreshMessagesRef = useRef(refreshMessages);
  const refreshAttachmentsRef = useRef(refreshAttachments);
  const convergenceKeyRef = useRef("");

  useEffect(() => {
    listAttachmentsForRORef.current = listAttachmentsForRO;
  }, [listAttachmentsForRO]);

  useEffect(() => {
    refreshMessagesRef.current = refreshMessages;
    refreshAttachmentsRef.current = refreshAttachments;
  }, [refreshAttachments, refreshMessages]);

  useEffect(() => {
    if (!isOpen || !roId) return;

    ensureMessages(roId).catch((err) => {
      console.error(err);
    });
  }, [ensureMessages, isOpen, roId]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isOpen]);

  useEffect(() => {
    const socketKey =
      realtime?.isLive === true ? safeString(realtime?.socketId || "live").trim() : "";
    const convergenceKey = isOpen && roId && socketKey ? `${roId}\n${socketKey}` : "";

    if (!convergenceKey) {
      convergenceKeyRef.current = "";
      return undefined;
    }

    if (convergenceKeyRef.current === convergenceKey) return undefined;

    convergenceKeyRef.current = convergenceKey;

    let cancelled = false;

    Promise.resolve(refreshMessagesRef.current?.(roId)).catch((err) => {
      if (!cancelled) console.error(err);
    });

    Promise.resolve(refreshAttachmentsRef.current?.()).catch((err) => {
      if (!cancelled) console.error(err);
    });

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    realtime?.socketId,
    realtime?.isLive,
    roId,
  ]);

  useEffect(() => {
    if (!isOpen || typeof realtime?.subscribeToRoAttachmentCreated !== "function") {
      return undefined;
    }

    return realtime.subscribeToRoAttachmentCreated((payload) => {
      const payloadRoId = safeString(payload?.roId).trim();
      const attachment = payload?.attachment;
      const attachmentId = safeString(attachment?.id).trim();

      if (payloadRoId !== roId || !attachmentId) return;
      if (retractedAttachmentIdsRef.current.has(attachmentId)) return;

      if (payload?.ro && typeof applyServerROSnapshot === "function") {
        applyServerROSnapshot(payload.ro);
      }

      setAttachments((current) =>
        filterKnownRetractedAttachments(mergeAttachmentsById(current, [attachment]))
      );
    });
  }, [applyServerROSnapshot, filterKnownRetractedAttachments, isOpen, realtime, roId]);

  useEffect(() => {
    if (!isOpen || typeof realtime?.subscribeToRoAttachmentRetracted !== "function") {
      return undefined;
    }

    return realtime.subscribeToRoAttachmentRetracted((payload) => {
      const payloadRoId = safeString(payload?.roId).trim();
      const attachmentId = safeString(payload?.attachmentId).trim();

      if (payloadRoId !== roId || !attachmentId) return;
      markAttachmentRetracted(attachmentId);
      if (payload?.ro && typeof applyServerROSnapshot === "function") {
        applyServerROSnapshot(payload.ro);
      }
      clearSignedUrlCache(attachmentId);
      setAttachments((current) =>
        (Array.isArray(current) ? current : []).filter(
          (attachment) => safeString(attachment?.id).trim() !== attachmentId
        )
      );
    });
  }, [applyServerROSnapshot, isOpen, markAttachmentRetracted, realtime, roId]);

  useEffect(() => {
    if (!isOpen || typeof realtime?.subscribeToRoMessageRetracted !== "function") {
      return undefined;
    }

    return realtime.subscribeToRoMessageRetracted((payload) => {
      const payloadRoId = safeString(payload?.roId).trim();

      if (payloadRoId !== roId) return;
      if (payload?.ro && typeof applyServerROSnapshot === "function") {
        applyServerROSnapshot(payload.ro);
      }
    });
  }, [applyServerROSnapshot, isOpen, realtime, roId]);

  useEffect(() => {
    setExpiredMessageIds(new Set());
    setExpiredAttachmentIds(new Set());
    attachmentRequestGenerationRef.current += 1;
    retractedAttachmentIdsRef.current = new Set();
    convergenceKeyRef.current = "";
  }, [isOpen, roId]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    function handleVisibilityChange() {
      setDocumentVisible(document.visibilityState === "visible");
    }

    handleVisibilityChange();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (
      !isOpen ||
      tab !== "thread" ||
      !roId ||
      !threadState.loaded ||
      !newestCanonicalMessageId ||
      !documentVisible ||
      !canMutateReadState
    ) {
      return;
    }

    markRead(roId, newestCanonicalMessageId).catch((err) => {
      console.error(err);
    });
  }, [
    canMutateReadState,
    documentVisible,
    isOpen,
    markRead,
    newestCanonicalMessageId,
    roId,
    tab,
    threadState.loaded,
  ]);

  useEffect(() => {
    setFailedUploadAttempt(null);
  }, [isOpen, roId]);

  useEffect(() => {
    let alive = true;

    async function loadAttachments() {
      if (!isOpen || !roId || typeof listAttachmentsForRORef.current !== "function") {
        if (alive) {
          setAttachments([]);
          setAttachmentError("");
          setAttachmentsLoading(false);
        }
        return;
      }

      try {
        setAttachmentsLoading(true);
        setAttachmentError("");

        const requestedRoId = roId;
        const requestGeneration = attachmentRequestGenerationRef.current + 1;
        attachmentRequestGenerationRef.current = requestGeneration;
        const next = await Promise.resolve(
          listAttachmentsForRORef.current(requestedRoId)
        );

        if (
          alive &&
          isOpen &&
          activeRoIdRef.current === requestedRoId &&
          attachmentRequestGenerationRef.current === requestGeneration
        ) {
          setAttachments((current) =>
            filterKnownRetractedAttachments(mergeAttachmentsById(current, next))
          );
        }
      } catch (err) {
        console.error(err);

        if (alive && activeRoIdRef.current === roId) {
          setAttachmentError("Unable to load photo/video attachments.");
          setAttachments([]);
        }
      } finally {
        if (alive && activeRoIdRef.current === roId) {
          setAttachmentsLoading(false);
        }
      }
    }

    loadAttachments();

    return () => {
      alive = false;
    };
  }, [filterKnownRetractedAttachments, isOpen, roId]);

  const threadItems = useMemo(
    () => buildThreadItems(notes, attachments, threadMessages),
    [notes, attachments, threadMessages]
  );

  if (!isOpen || !ro) return null;

  const tag = getTag(ro);
  const advisor = getAdvisorName(ro);
  const tech = getTechNameForRO(ro, getTechName);
  const status = getStatusText(ro);

  void actorRole;
  void actorName;

  function handleSaveNote() {
    if (submitLockRef.current) return;

    const text = safeString(draft).trim();
    if (!text || busy || drawerReadOnly || !canSendMessages) return;

    if (text.length > maxBodyLength) {
      alert("Notes must be 4000 characters or fewer.");
      return;
    }

    submitLockRef.current = true;
    setBusy(true);

    try {
      sendMessage(ro.id, text, {
        onConfirmed:
          typeof onMessageConfirmed === "function"
            ? onMessageConfirmed
            : undefined,
      });
      setDraft("");
    } catch (err) {
      console.error(err);
      alert(safeString(err?.message).trim() || "Unable to save note.");
    } finally {
      submitLockRef.current = false;
      setBusy(false);
    }
  }

  function handleRetryMessage(message) {
    if (drawerReadOnly || !message?.localId) return;

    try {
      retryMessage(ro.id, message.localId);
    } catch (err) {
      console.error(err);
    }
  }

  async function handleRetractMessage(message) {
    const messageId = safeString(message?.id).trim();
    if (!messageId || drawerReadOnly || retractingMessageId) return;

    try {
      setUndoError("");
      setRetractingMessageId(messageId);
      const data = await Promise.resolve(retractMessage(roId, messageId));
      if (data?.ro && typeof applyServerROSnapshot === "function") {
        applyServerROSnapshot(data.ro);
      }
    } catch (err) {
      console.error(err);
      if (err?.data?.code === "UNDO_SEND_EXPIRED") {
        setExpiredMessageIds((current) => {
          const next = new Set(current);
          next.add(messageId);
          return next;
        });
      }
      setUndoError(
        safeString(err?.data?.error || err?.message).trim() ||
          "Unable to undo send."
      );
    } finally {
      setRetractingMessageId("");
    }
  }

  function handleLoadOlderMessages() {
    Promise.resolve(loadOlderMessages(roId)).catch((err) => {
      console.error(err);
    });
  }

  async function handleRetractAttachment(attachment) {
    const attachmentId = safeString(attachment?.id).trim();
    if (
      !attachmentId ||
      drawerReadOnly ||
      retractingAttachmentId ||
      typeof retractAttachment !== "function"
    ) {
      return;
    }

    try {
      setUndoError("");
      setRetractingAttachmentId(attachmentId);
      const data = await Promise.resolve(retractAttachment(attachmentId));
      markAttachmentRetracted(attachmentId);
      if (data?.ro && typeof applyServerROSnapshot === "function") {
        applyServerROSnapshot(data.ro);
      }
      clearSignedUrlCache(attachmentId);
      setAttachments((current) =>
        (Array.isArray(current) ? current : []).filter(
          (item) => safeString(item?.id).trim() !== attachmentId
        )
      );
    } catch (err) {
      console.error(err);
      if (err?.data?.code === "UNDO_SEND_EXPIRED") {
        setExpiredAttachmentIds((current) => {
          const next = new Set(current);
          next.add(attachmentId);
          return next;
        });
      }
      setUndoError(
        safeString(err?.data?.error || err?.message).trim() ||
          "Unable to undo send."
      );
    } finally {
      setRetractingAttachmentId("");
    }
  }

  async function runUploadAttempt(attempt) {
    if (!roId) {
      alert("Missing RO id.");
      return;
    }

    if (typeof uploadAttachmentsForRO !== "function") {
      alert("Missing attachment upload handler.");
      return;
    }

    try {
      setUploadBusy(true);
      setAttachmentError("");

      await Promise.resolve(
        uploadAttachmentsForRO(roId, attempt.files, {
          uploadRequestId: attempt.uploadRequestId,
        })
      );
      setFailedUploadAttempt(null);

      try {
        await refreshAttachments();
      } catch (refreshError) {
        console.error(refreshError);
        setAttachmentError(
          "Upload saved, but the attachment list could not be refreshed."
        );
      }

      setTab("thread");
    } catch (err) {
      console.error(err);
      const message =
        safeString(err?.message).trim() || "Unable to upload attachment.";

      if (isRetryableAttachmentUploadError(err)) {
        setFailedUploadAttempt({
          ...attempt,
          roId,
          errorMessage: message,
        });
      } else {
        setFailedUploadAttempt(null);
      }

      setAttachmentError(
        message
      );
      alert(
        `Unable to upload photo/video.\n\n${safeString(message).slice(
          0,
          500
        )}`
      );
    } finally {
      setUploadBusy(false);
    }
  }

  async function handleFilesSelected(e) {
    const selected = Array.from(e.target.files || []).filter(Boolean);
    e.target.value = "";

    if (!selected.length || drawerReadOnly) return;

    const attempt = {
      roId,
      uploadRequestId: createAttachmentUploadRequestId(),
      files: selected,
    };

    setFailedUploadAttempt(null);
    await runUploadAttempt(attempt);
  }

  function handleRetryUpload() {
    if (!failedUploadAttempt || uploadBusy) return;
    if (failedUploadAttempt.roId !== roId) {
      setFailedUploadAttempt(null);
      return;
    }
    runUploadAttempt(failedUploadAttempt);
  }

  function handleOverlayClick(e) {
    if (e.target === e.currentTarget && typeof onClose === "function") {
      onClose();
    }
  }

  return (
    <div style={overlayStyle()} onMouseDown={handleOverlayClick}>
      <aside style={drawerStyle()} onMouseDown={(e) => e.stopPropagation()}>
        <header style={headerStyle()}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) auto",
              gap: 12,
              alignItems: "start",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "#64748b",
                }}
              >
                {title}
              </div>

              <div
                style={{
                  marginTop: 3,
                  fontSize: 23,
                  lineHeight: 1.08,
                  fontWeight: 950,
                  color: "#111827",
                  letterSpacing: "-0.02em",
                }}
              >
                {getROLabel(ro)}
              </div>

              <div
                style={{
                  marginTop: 5,
                  color: "#4b5563",
                  fontSize: 13,
                  lineHeight: 1.35,
                }}
              >
                {getCustomerVehicleLine(ro)}
              </div>
            </div>

            <button type="button" onClick={onClose} style={closeButtonStyle()}>
              Close
            </button>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              alignItems: "center",
            }}
          >
            {tag ? <span style={metaPillStyle()}>Tag #{tag}</span> : null}
            {advisor ? <span style={metaPillStyle()}>Advisor: {advisor}</span> : null}
            {tech ? <span style={metaPillStyle()}>Tech: {tech}</span> : null}
            <span style={metaPillStyle()}>{status}</span>
          </div>

          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => setTab("thread")}
              style={tabButtonStyle(tab === "thread")}
            >
              Notes Thread
            </button>

            <button
              type="button"
              onClick={() => setTab("activity")}
              style={tabButtonStyle(tab === "activity")}
            >
              Activity Timeline
            </button>
          </div>
        </header>

        <main
          style={{
            padding: 14,
            overflow: "auto",
            background: "#f8fafc",
          }}
        >
          {tab === "thread" ? (
            <div style={{ display: "grid", gap: 10 }}>
              {attachmentError ? (
                <div style={{ ...emptyStateStyle(), display: "grid", gap: 8 }}>
                  <div>{attachmentError}</div>
                  {failedUploadAttempt?.roId === roId ? (
                    <div>
                      <button
                        type="button"
                        onClick={handleRetryUpload}
                        disabled={uploadBusy}
                        style={secondaryButtonStyle(uploadBusy)}
                      >
                        Retry Upload
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {threadState.loadError ? (
                <div style={emptyStateStyle()}>Couldn{"\u2019"}t load messages.</div>
              ) : null}

              {undoError ? (
                <div style={emptyStateStyle()}>{undoError}</div>
              ) : null}

              {threadState.hasMore ? (
                <div>
                  <button
                    type="button"
                    onClick={handleLoadOlderMessages}
                    disabled={threadState.loading || threadState.refreshing}
                    style={secondaryButtonStyle(
                      threadState.loading || threadState.refreshing
                    )}
                  >
                    Load older
                  </button>
                </div>
              ) : null}

              {threadState.loading && !threadItems.length ? (
                <div style={emptyStateStyle()}>Loading messages...</div>
              ) : null}

              {attachmentsLoading && !threadItems.length ? (
                <div style={emptyStateStyle()}>Loading photo/video attachments...</div>
              ) : null}

              {threadItems.length ? (
                threadItems.map((item) => {
                  if (item.kind === "attachment") {
                    return (
                      <AttachmentBubble
                        key={item.id}
                        attachment={item.attachment}
                        getSignedUrl={getAttachmentSignedUrl}
                        currentUserId={currentUserId}
                        readOnly={drawerReadOnly}
                        nowMs={nowMs}
                        retracting={
                          retractingAttachmentId ===
                          safeString(item.attachment?.id).trim()
                        }
                        expired={expiredAttachmentIds.has(
                          safeString(item.attachment?.id).trim()
                        )}
                        onRetract={handleRetractAttachment}
                      />
                    );
                  }

                  if (item.kind === "message") {
                    return (
                      <MessageBubble
                        key={item.id}
                        message={item.message}
                        readOnly={drawerReadOnly}
                        onRetry={handleRetryMessage}
                        onRetract={handleRetractMessage}
                        currentUserId={currentUserId}
                        nowMs={nowMs}
                        retracting={
                          retractingMessageId ===
                          safeString(item.message?.id).trim()
                        }
                        expired={expiredMessageIds.has(
                          safeString(item.message?.id).trim()
                        )}
                      />
                    );
                  }

                  const note = item.note;
                  const parsed = item.parsed || parseNoteHeader(note.header);
                  const displayTime = formatWhen(parsed.when) || parsed.when;

                  return (
                    <div key={item.id} style={noteBubbleStyle()}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 10,
                          alignItems: "baseline",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 950,
                            color: "#111827",
                          }}
                        >
                          {parsed.author}
                        </div>

                        {displayTime ? (
                          <div
                            style={{
                              fontSize: 11,
                              color: "#64748b",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {displayTime}
                          </div>
                        ) : null}
                      </div>

                      <div
                        style={{
                          marginTop: 6,
                          fontSize: 13,
                          color: "#374151",
                          lineHeight: 1.45,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {note.body || note.raw}
                      </div>
                    </div>
                  );
                })
              ) : !attachmentsLoading ? (
                <div style={emptyStateStyle()}>
                  No shared notes, photos, or videos yet. Add the first update so
                  the team can see what is happening with this RO.
                </div>
              ) : null}
            </div>
          ) : (
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 14,
                background: "#ffffff",
                padding: 12,
              }}
            >
              <ROStatusTimeline
                ro={ro}
                getTechName={getTechName}
                title="Activity Timeline"
                emptyMessage="No activity has been recorded yet."
              />
            </div>
          )}
        </main>

        <footer
          style={{
            padding: 14,
            borderTop: "1px solid #e5e7eb",
            background: "#ffffff",
            display: "grid",
            gap: 9,
          }}
        >
          {drawerReadOnly ? (
            <div style={emptyStateStyle()}>
              This thread is read-only from this view.
            </div>
          ) : (
            <>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Add a shared note for this RO..."
                style={textareaStyle()}
              />

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,video/mp4,video/quicktime,video/webm"
                onChange={handleFilesSelected}
                style={{ display: "none" }}
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 10,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ fontSize: 11.5, color: "#64748b" }}>
                  Notes, photos, videos, and actions stay attached to this RO.
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadBusy || busy || drawerReadOnly}
                    style={secondaryButtonStyle(uploadBusy || busy || drawerReadOnly)}
                  >
                    {uploadBusy ? "Uploading..." : "Attach Photo / Video"}
                  </button>

                  <button
                    type="button"
                    onClick={handleSaveNote}
                    disabled={sendDisabled}
                    style={primaryButtonStyle(sendDisabled)}
                  >
                    {busy ? "Sending\u2026" : "Add Note"}
                  </button>
                </div>
              </div>
            </>
          )}
        </footer>
      </aside>
    </div>
  );
}
